import { useSelector } from 'react-redux';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  deleteArea,
  deleteSquare,
  deleteCircle,
  getDrawingToolControlData,
  getSelectedArea,
  getSelectedSquare,
  getSelectedCircle,
  setSelectedArea,
  setSelectedSquare,
  setSelectedCircle,
  updateArea,
  updateSquare,
  updateCircle,
} from 'store/lixaniPaint/slices/drawingToolSlice';
import Button from 'components/Button/Button';
import { rgbaToHex } from '../utils/rgbaToHex';
import { selectIsLixaniPaintDialogOpen } from 'store/lixaniPaint/selectors/lixani-paint.selectors';

export const AreaSettings = () => {
  const dispatch = useDispatch();

  const controlData = useSelector(getDrawingToolControlData);
  const selectedArea = useSelector(getSelectedArea);
  const selectedSquare = useSelector(getSelectedSquare);
  const selectedCircle = useSelector(getSelectedCircle);

  const isLixaniPaintDialogOpen = useSelector(selectIsLixaniPaintDialogOpen);

  const selectedElement = selectedArea || selectedSquare || selectedCircle;

  const handleFillColorChange = (e) => {
    if (!selectedElement) return;
    const hexColor = e.target.value;
    const opacity =
      selectedElement?.fillOpacity !== undefined
        ? selectedElement.fillOpacity
        : controlData.fillOpacity !== undefined
        ? controlData.fillOpacity
        : 0.3;
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const rgbaColor = `rgba(${r}, ${g}, ${b}, ${opacity})`;

    const updated = { ...selectedElement, fillColor: rgbaColor };
    if (selectedArea) {
      dispatch(setSelectedArea(updated));
      dispatch(updateArea(updated));
    } else if (selectedSquare) {
      dispatch(setSelectedSquare(updated));
      dispatch(updateSquare(updated));
    } else if (selectedCircle) {
      dispatch(setSelectedCircle(updated));
      dispatch(updateCircle(updated));
    }
  };

  const handleStrokeColorChange = (e) => {
    if (!selectedElement) return;

    const updated = { ...selectedElement, lineColor: e.target.value };
    if (selectedArea) {
      dispatch(setSelectedArea(updated));
      dispatch(updateArea(updated));
    } else if (selectedSquare) {
      dispatch(setSelectedSquare(updated));
      dispatch(updateSquare(updated));
    } else if (selectedCircle) {
      dispatch(setSelectedCircle(updated));
      dispatch(updateCircle(updated));
    }
  };

  const handleLineWidthChange = (e) => {
    if (!selectedElement) return;

    const updated = { ...selectedElement, lineWidth: parseInt(e.target.value) };
    if (selectedArea) {
      dispatch(setSelectedArea(updated));
      dispatch(updateArea(updated));
    } else if (selectedSquare) {
      dispatch(setSelectedSquare(updated));
      dispatch(updateSquare(updated));
    } else if (selectedCircle) {
      dispatch(setSelectedCircle(updated));
      dispatch(updateCircle(updated));
    }
  };

  const handleOpacityChange = (e) => {
    if (!selectedElement) return;

    const newOpacity = parseFloat(e.target.value);
    const currentFillColor =
      selectedElement?.fillColor || controlData.fillColor;

    let r, g, b;
    if (currentFillColor.startsWith('rgba')) {
      const match = currentFillColor.match(
        /rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/
      );
      [, r, g, b] = match;
    } else {
      r = parseInt(currentFillColor.slice(1, 3), 16);
      g = parseInt(currentFillColor.slice(3, 5), 16);
      b = parseInt(currentFillColor.slice(5, 7), 16);
    }

    const rgbaColor = `rgba(${r}, ${g}, ${b}, ${newOpacity})`;
    const updated = {
      ...selectedElement,
      fillOpacity: newOpacity,
      fillColor: rgbaColor,
    };

    if (selectedArea) {
      dispatch(setSelectedArea(updated));
      dispatch(updateArea(updated));
    } else if (selectedSquare) {
      dispatch(setSelectedSquare(updated));
      dispatch(updateSquare(updated));
    } else if (selectedCircle) {
      dispatch(setSelectedCircle(updated));
      dispatch(updateCircle(updated));
    }
  };

  const handleCircleSizeChange = (e) => {
    if (!selectedCircle) return;

    const newSize = parseInt(e.target.value);
    const updated = {
      ...selectedCircle,
      radiusX: newSize,
      radiusY: newSize,
    };

    dispatch(setSelectedCircle(updated));
    dispatch(updateCircle(updated));
  };

  const handleDelete = () => {
    if (selectedArea) {
      dispatch(setSelectedArea(null));
      dispatch(deleteArea({ id: selectedArea.id }));
    } else if (selectedSquare) {
      dispatch(setSelectedSquare(null));
      dispatch(deleteSquare({ id: selectedSquare.id }));
    } else if (selectedCircle) {
      dispatch(setSelectedCircle(null));
      dispatch(deleteCircle({ id: selectedCircle.id }));
    }
  };

  return (
    <div
      style={{
        padding: isLixaniPaintDialogOpen ? '0px' : '16px',
        display: isLixaniPaintDialogOpen ? 'flex' : 'block',
        gap: '5px',
        color: 'white',
        flexWrap: 'wrap',
      }}
    >
      {!(selectedCircle && isLixaniPaintDialogOpen) && (
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Alueen väri
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="color"
              value={
                rgbaToHex(selectedElement?.fillColor) ||
                rgbaToHex(controlData?.fillColor)
              }
              onChange={handleFillColorChange}
              style={{
                width: '40px',
                height: '32px',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: '13px', opacity: 0.8 }}>
              {rgbaToHex(selectedElement?.fillColor) ||
                rgbaToHex(controlData?.fillColor)}
            </span>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Rajojen väri
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <input
            type="color"
            value={selectedElement?.lineColor || controlData?.lineColor}
            onChange={handleStrokeColorChange}
            style={{
              width: '40px',
              height: '32px',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
            }}
          />
          <span style={{ fontSize: '13px', opacity: 0.8 }}>
            {selectedElement?.lineColor || controlData?.lineColor}
          </span>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Rajojen leveys
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <input
            type="range"
            min="1"
            max="10"
            value={selectedElement?.lineWidth || controlData?.lineWidth}
            onChange={handleLineWidthChange}
            style={{
              flex: 1,
              height: '6px',
              borderRadius: '3px',
              background: 'rgba(255,255,255,0.2)',
              outline: 'none',
              cursor: 'pointer',
            }}
          />
          <span
            style={{
              minWidth: '40px',
              fontSize: '13px',
              fontWeight: '500',
              padding: '4px 8px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            {selectedElement?.lineWidth || 2}px
          </span>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          {selectedCircle && isLixaniPaintDialogOpen ? 'Koko' : 'Läpinäkyvyys'}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {selectedCircle && isLixaniPaintDialogOpen ? (
            <input
              type="range"
              min="10"
              max="200"
              step="1"
              value={selectedCircle.radiusX || 20}
              onChange={handleCircleSizeChange}
              style={{
                flex: 1,
                height: '6px',
                borderRadius: '3px',
                background: 'rgba(255,255,255,0.2)',
                outline: 'none',
                cursor: 'pointer',
              }}
            />
          ) : (
            <>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={
                  selectedElement?.fillOpacity !== undefined
                    ? selectedElement.fillOpacity
                    : controlData.fillOpacity !== undefined
                    ? controlData.fillOpacity
                    : 0.3
                }
                onChange={handleOpacityChange}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '3px',
                  background: 'rgba(255,255,255,0.2)',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              />
              <span
                style={{
                  minWidth: '40px',
                  fontSize: '13px',
                  fontWeight: '500',
                  padding: '4px 8px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: '4px',
                  textAlign: 'center',
                }}
              >
                {Math.round(
                  (selectedElement?.fillOpacity !== undefined
                    ? selectedElement.fillOpacity
                    : controlData.fillOpacity !== undefined
                    ? controlData.fillOpacity
                    : 0.3) * 100
                )}%
              </span>
            </>
          )}
        </div>
      </div>

      {selectedElement && (
        <Button
          onClick={handleDelete}
          color="danger"
          fullWidth
          size="small"
          style={{ marginTop: '8px' }}
        >
          Poista {selectedArea ? 'alue' : selectedSquare ? 'neliö' : 'ympyrä'}
        </Button>
      )}
    </div>
  );
};